import { useState, useEffect } from 'react';
import { Loader2, Package, AlertTriangle, CheckCircle, XCircle, Edit2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin/shop';

export default function AdminShopInventoryPage() {
  const [inventory, setInventory] = useState({ stats: {}, products: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editingStock, setEditingStock] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchInventory();
  }, [filter]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_admin_token')}` }
  });

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const params = filter ? `?stock_status=${filter}` : '';
      const response = await axios.get(`${API}/inventory${params}`, getAuthHeaders());
      setInventory(response.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const updateStock = async (productId, newStock) => {
    setSaving({ ...saving, [productId]: true });
    try {
      await axios.put(`${API}/inventory/${productId}?stock=${newStock}`, {}, getAuthHeaders());
      toast.success('Stock updated');
      setEditingStock({ ...editingStock, [productId]: undefined });
      fetchInventory();
    } catch (error) {
      toast.error('Failed to update stock');
    } finally {
      setSaving({ ...saving, [productId]: false });
    }
  };

  const getStockIndicator = (stock) => {
    if (stock === 0) return { color: 'bg-red-500', text: 'Out of Stock', icon: XCircle };
    if (stock <= 10) return { color: 'bg-orange-500', text: 'Low Stock', icon: AlertTriangle };
    return { color: 'bg-green-500', text: 'In Stock', icon: CheckCircle };
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Inventory</h1>
        <p className="text-slate-500 text-sm mt-1">Monitor and manage stock levels</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{inventory.stats.total || 0}</p>
              <p className="text-sm text-slate-500">Total Products</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 cursor-pointer hover:border-green-300" onClick={() => setFilter('in_stock')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{inventory.stats.in_stock || 0}</p>
              <p className="text-sm text-slate-500">In Stock</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 cursor-pointer hover:border-orange-300" onClick={() => setFilter('low_stock')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{inventory.stats.low_stock || 0}</p>
              <p className="text-sm text-slate-500">Low Stock</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 cursor-pointer hover:border-red-300" onClick={() => setFilter('out_of_stock')}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{inventory.stats.out_of_stock || 0}</p>
              <p className="text-sm text-slate-500">Out of Stock</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {['', 'in_stock', 'low_stock', 'out_of_stock'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}>
            {f === '' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : inventory.products.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No products found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Product</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">SKU</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Category</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Stock</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Reserved</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Available</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Sold</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {inventory.products.map((product) => {
                const indicator = getStockIndicator(product.stock);
                const isEditing = editingStock[product.id] !== undefined;
                const Icon = indicator.icon;
                
                return (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{product.name}</td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-500">{product.sku || 'N/A'}</td>
                    <td className="px-4 py-3 text-slate-600">{product.category_name}</td>
                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <input type="number" value={editingStock[product.id]} min="0"
                          onChange={(e) => setEditingStock({...editingStock, [product.id]: parseInt(e.target.value) || 0})}
                          className="w-20 px-2 py-1 border border-amber-300 rounded text-center" autoFocus />
                      ) : (
                        <span className="font-semibold">{product.stock}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-500">{product.reserved_stock || 0}</td>
                    <td className="px-4 py-3 text-center font-medium">{product.available_stock}</td>
                    <td className="px-4 py-3 text-center text-slate-500">{product.sold_count || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${indicator.color.replace('bg-', 'bg-').replace('500', '100')} ${indicator.color.replace('bg-', 'text-').replace('500', '700')} text-xs rounded-full`}>
                        <Icon className="w-3 h-3" />
                        {indicator.text}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditingStock({...editingStock, [product.id]: undefined})}
                            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400">
                            <XCircle className="w-4 h-4" />
                          </button>
                          <button onClick={() => updateStock(product.id, editingStock[product.id])}
                            disabled={saving[product.id]}
                            className="p-1.5 bg-amber-500 rounded-lg text-white hover:bg-amber-600">
                            {saving[product.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setEditingStock({...editingStock, [product.id]: product.stock})}
                          className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
